import { assertNever } from "@impero-com/common/utils/obj";

import {
    CalendarView,
    ControlProgramOverview,
    UserTagCategoryOverview,
    UserTagOverview,
} from "api/generated/model";
import {
    FIRST_DATA_ROW,
    RECURRENCE_TYPES_REQUIRING_OFFSET_DAYS,
} from "org/control/bulkImport/process/constants";
import { OperationResult, RowError } from "utils/spreadsheet/types";

import {
    RawControlRow,
    ValidControlRow,
    ValidWeeklyControlRow,
    ValidDateControlRow,
    ValidMonthlyControlRow,
    ValidQuarterlyControlRow,
    ValidEveryTwoMonthsControlRow,
    ValidHalfYearlyControlRow,
    ValidYearlyControlRow,
} from "./types";

// -------
// TYPES
// -------

// Context required for validating control import rows
export interface ValidationContext {
    controlPrograms: ControlProgramOverview[];
    tagCategories: UserTagCategoryOverview[];
    tags: UserTagOverview[];
    calendars: CalendarView[];
}

// Resolved control data populated during validation from the context
interface ResolvedControlData {
    controlProgram?: ControlProgramOverview;
    calendar?: CalendarView;
    tags?: UserTagOverview[];
}

// Define a type for the result of each validator
interface ValidationResult {
    errors: RowError[];
    resolvedData: Partial<ResolvedControlData>;
}

// ----------------------------
// MAIN EXPORT FUNCTION
// ----------------------------

/**
 * Validates and transforms an array of raw control rows into enriched ValidControlRow objects
 *
 * @param rows - Array of RawControlRow objects extracted from the spreadsheet
 * @param context - ValidationContext containing reference data needed for validation
 * @returns OperationResult containing either validated control rows or validation errors
 */
export function validateControlRows(
    rows: RawControlRow[],
    context: ValidationContext
): OperationResult<ValidControlRow[]> {
    const validRows: ValidControlRow[] = [];
    const errors: string[] = [];

    rows.forEach((row, index) => {
        const result = validateAndTransformRow(row, index, context);

        if (result.success) {
            validRows.push(result.data);
        } else {
            errors.push(...result.errors);
        }
    });

    if (errors.length > 0) {
        return {
            success: false,
            errors,
        };
    }

    return {
        success: true,
        data: validRows,
    };
}

/**
 * Validates a single control row and transforms it if valid
 *
 * @param row - The raw control row to validate
 * @param index - The index of the row in the original array
 * @param context - The validation context
 * @returns Either the valid transformed row or validation errors
 */
function validateAndTransformRow(
    row: RawControlRow,
    index: number,
    context: ValidationContext
): OperationResult<ValidControlRow> {
    const validators = [
        validateControlProgram,
        validateCalendar,
        validateTags,
        validateRecurrenceOffsetDays,
        validateRecurrenceTypeSpecificFields,
        validateNonApplicableRecurrenceFields,
    ];

    const validationResult = validators.reduce<{
        errors: RowError[];
        resolvedData: ResolvedControlData;
    }>(
        (acc, validator) => {
            const result = validator(row, index, context);

            if (result.errors.length > 0) {
                acc.errors.push(...result.errors);
            }

            acc.resolvedData = {
                ...acc.resolvedData,
                ...result.resolvedData,
            };

            return acc;
        },
        { errors: [], resolvedData: {} }
    );

    if (validationResult.errors.length > 0) {
        return {
            success: false,
            errors: validationResult.errors.map(
                (err) => `Row ${err.row}: ${err.field} - ${err.message}`
            ),
        };
    }

    return {
        success: true,
        data: transformToValidControlRow(row, validationResult.resolvedData),
    };
}

// ------------
// VALIDATORS
// ------------

/**
 * Validates that the control program exists
 */
const validateControlProgram = (
    row: RawControlRow,
    index: number,
    context: ValidationContext
): ValidationResult => {
    const controlProgram = context.controlPrograms.find(
        (cp) => cp.name === row.ControlProgramName
    );

    if (!controlProgram) {
        return {
            errors: [
                {
                    row: index + FIRST_DATA_ROW,
                    field: "ControlProgramName",
                    message: `Control program "${row.ControlProgramName}" does not exist`,
                },
            ],
            resolvedData: {},
        };
    }

    return {
        errors: [],
        resolvedData: { controlProgram },
    };
};

/**
 * Validates that the calendar exists if provided
 */
const validateCalendar = (
    row: RawControlRow,
    index: number,
    context: ValidationContext
): ValidationResult => {
    // If Calendar is not provided or is an empty string, it's valid
    if (!row.Calendar || row.Calendar.trim() === "") {
        return { errors: [], resolvedData: {} };
    }

    const calendarName = row.Calendar.trim();
    const calendar = context.calendars.find((cal) => cal.name === calendarName);

    if (!calendar) {
        return {
            errors: [
                {
                    row: index + FIRST_DATA_ROW,
                    field: "Calendar",
                    message: `Calendar "${calendarName}" does not exist`,
                },
            ],
            resolvedData: {},
        };
    }

    return {
        errors: [],
        resolvedData: { calendar },
    };
};

/**
 * Validates that all tags exist in their respective categories
 */
const validateTags = (
    row: RawControlRow,
    index: number,
    context: ValidationContext
): ValidationResult => {
    if (!row.tags) {
        return { errors: [], resolvedData: {} };
    }

    const errors: RowError[] = [];
    const tags: UserTagOverview[] = [];

    for (const tagColumn of row.tags) {
        const category = context.tagCategories.find(
            (c) => c.name === tagColumn.category && c.active
        );
        if (!category) {
            errors.push({
                row: index + FIRST_DATA_ROW,
                field: `ControlTags: ${tagColumn.category}`,
                message: `Tag category "${tagColumn.category}" does not exist`,
            });
            // eslint-disable-next-line no-continue
            continue;
        }

        const validTagsInCategory = context.tags.filter(
            (tag) => tag.tagCategoryId === category.id && tag.active
        );

        const tagMap = new Map(
            validTagsInCategory.map((tag) => [tag.name, tag])
        );

        const matchedTags: UserTagOverview[] = [];

        for (const tagName of tagColumn.tags) {
            const tag = tagMap.get(tagName);
            if (!tag) {
                errors.push({
                    row: index + FIRST_DATA_ROW,
                    field: `ControlTags: ${tagColumn.category}`,
                    message: `Tag "${tagName}" does not exist in category "${tagColumn.category}"`,
                });
            } else {
                matchedTags.push(tag);
            }
        }

        if (matchedTags.length > 0) {
            tags.push(...matchedTags);
        }
    }

    return {
        errors,
        resolvedData: tags.length > 0 ? { tags } : { tags: [] },
    };
};

/**
 * Validates that RecurrenceOffsetDays is set for recurrence types that require it
 */
const validateRecurrenceOffsetDays = (
    row: RawControlRow,
    index: number
): ValidationResult => {
    const recurrenceType = row.RecurrenceType;
    const recurrenceOffsetDays = row.RecurrenceOffsetDays;

    const requiresOffsetDays =
        RECURRENCE_TYPES_REQUIRING_OFFSET_DAYS.includes(recurrenceType);
    if (requiresOffsetDays && !recurrenceOffsetDays) {
        return {
            errors: [
                {
                    row: index + FIRST_DATA_ROW,
                    field: "RecurrenceOffsetDays",
                    message: `RecurrenceOffsetDays is required for ${recurrenceType} recurrence type`,
                },
            ],
            resolvedData: {},
        };
    }
    return { errors: [], resolvedData: {} };
};

/**
 * Validates that required fields for specific recurrence types are set
 */
const validateRecurrenceTypeSpecificFields = (
    row: RawControlRow,
    index: number
): ValidationResult => {
    const recurrenceType = row.RecurrenceType;
    const errors: RowError[] = [];

    switch (recurrenceType) {
        case "Weekly":
            if (
                !row.WeeklyRecurrenceDays ||
                row.WeeklyRecurrenceDays.length === 0
            ) {
                errors.push({
                    row: index + FIRST_DATA_ROW,
                    field: "WeeklyRecurrenceDays",
                    message:
                        "WeeklyRecurrenceDays is required for Weekly recurrence type",
                });
            }
            break;

        case "Monthly":
            if (
                !row.MonthlyRecurrenceMonths ||
                row.MonthlyRecurrenceMonths.length === 0
            ) {
                errors.push({
                    row: index + FIRST_DATA_ROW,
                    field: "MonthlyRecurrenceMonths",
                    message:
                        "MonthlyRecurrenceMonths is required for Monthly recurrence type",
                });
            }
            break;

        case "EveryTwoMonths":
            if (
                !row.EveryTwoMonthsRecurrenceTwoMonths ||
                row.EveryTwoMonthsRecurrenceTwoMonths.length === 0
            ) {
                errors.push({
                    row: index + FIRST_DATA_ROW,
                    field: "EveryTwoMonthsRecurrenceTwoMonths",
                    message:
                        "EveryTwoMonthsRecurrenceTwoMonths is required for EveryTwoMonths recurrence type",
                });
            }
            break;

        case "Quarterly":
            if (
                !row.QuarterlyRecurrenceQuarters ||
                row.QuarterlyRecurrenceQuarters.length === 0
            ) {
                errors.push({
                    row: index + FIRST_DATA_ROW,
                    field: "QuarterlyRecurrenceQuarters",
                    message:
                        "QuarterlyRecurrenceQuarters is required for Quarterly recurrence type",
                });
            }
            break;

        case "HalfYearly":
            if (
                !row.HalfYearlyRecurrenceHalfYears ||
                row.HalfYearlyRecurrenceHalfYears.length === 0
            ) {
                errors.push({
                    row: index + FIRST_DATA_ROW,
                    field: "HalfYearlyRecurrenceHalfYears",
                    message:
                        "HalfYearlyRecurrenceHalfYears is required for HalfYearly recurrence type",
                });
            }
            break;

        case "Date":
            if (!row.DateRecurrenceDay || row.DateRecurrenceDay.length === 0) {
                errors.push({
                    row: index + FIRST_DATA_ROW,
                    field: "DateRecurrenceDay",
                    message:
                        "DateRecurrenceDay is required for Date recurrence type",
                });
            }
            break;
    }

    return {
        errors,
        resolvedData: {},
    };
};

/**
 * Validates that fields for other recurrence types are not populated
 */
const validateNonApplicableRecurrenceFields = (
    row: RawControlRow,
    index: number
): ValidationResult => {
    const recurrenceType = row.RecurrenceType;
    const errors: RowError[] = [];

    const recurrenceTypeFields: Record<string, Array<keyof RawControlRow>> = {
        Date: ["DateRecurrenceDay"],
        Weekly: ["WeeklyRecurrenceDays", "WeeklyRecurrenceFrequency"],
        Monthly: ["MonthlyRecurrenceMonths", "RecurrenceOffsetDays"],
        EveryTwoMonths: [
            "EveryTwoMonthsRecurrenceTwoMonths",
            "RecurrenceOffsetDays",
        ],
        Quarterly: ["QuarterlyRecurrenceQuarters", "RecurrenceOffsetDays"],
        HalfYearly: ["HalfYearlyRecurrenceHalfYears", "RecurrenceOffsetDays"],
        Yearly: ["RecurrenceOffsetDays"],
    };

    const allFields = Object.values(recurrenceTypeFields).flat();
    const relevantFields = new Set(recurrenceTypeFields[recurrenceType]);

    // Keep track of reported fields to avoid duplicate errors
    const reportedFields = new Set<string>();

    allFields.forEach((field) => {
        if (
            relevantFields.has(field) ||
            field === "RecurrenceType" ||
            field === "RecurrenceStartDate"
        ) {
            return;
        }

        const value = row[field];

        if (
            (Array.isArray(value) && value.length > 0) ||
            (typeof value === "string" && value.trim() !== "") ||
            (typeof value === "number" && value !== 0) ||
            (typeof value === "boolean" && value !== false) ||
            (value !== undefined && value !== null && value !== "")
        ) {
            if (!reportedFields.has(field)) {
                errors.push({
                    row: index + FIRST_DATA_ROW,
                    field,
                    message: `${field} should not be set for ${recurrenceType} recurrence type`,
                });
                reportedFields.add(field);
            }
        }
    });

    return {
        errors,
        resolvedData: {},
    };
};

// ----------------------
// DATA TRANSFORMATION
// ----------------------

/**
 * Transforms a validated ControlRow into a ValidControlRow using resolved objects
 * from the validation phase
 * Provide type narrowing for each recurrence type
 */
function transformToValidControlRow(
    row: RawControlRow,
    enrichedData: ResolvedControlData
): ValidControlRow {
    const { controlProgram, calendar, tags } = enrichedData;

    const baseValidRow = {
        title: row.Title,
        controlProgram: controlProgram!,
        description: row.Description,
        recurrenceStartDate: row.RecurrenceStartDate,
        notificationSentDaysBefore: row.NotificationSentDaysBefore ?? 5,
        loginRequired: row.LoginRequired,
        coveredPeriod: row.CoveredPeriod,
        calendar,
        tags,
        tasks: row.tasks,
        firstReviewerTasks: row.firstReviewerTasks,
        secondReviewerTasks: row.secondReviewerTasks,
    };

    switch (row.RecurrenceType) {
        case "Date":
            return {
                ...baseValidRow,
                recurrenceType: "Date",
                dateRecurrenceDay: row.DateRecurrenceDay!,
            } satisfies ValidDateControlRow;

        case "Weekly":
            return {
                ...baseValidRow,
                recurrenceType: "Weekly",
                weeklyRecurrenceDays: row.WeeklyRecurrenceDays!,
                weeklyRecurrenceFrequency: row.WeeklyRecurrenceFrequency ?? 1,
            } satisfies ValidWeeklyControlRow;

        case "Monthly":
            return {
                ...baseValidRow,
                recurrenceType: "Monthly",
                monthlyRecurrenceMonths: row.MonthlyRecurrenceMonths!,
                recurrenceOffsetDays: row.RecurrenceOffsetDays!,
            } satisfies ValidMonthlyControlRow;

        case "EveryTwoMonths":
            return {
                ...baseValidRow,
                recurrenceType: "EveryTwoMonths",
                everyTwoMonthsRecurrenceTwoMonths:
                    row.EveryTwoMonthsRecurrenceTwoMonths!,
                recurrenceOffsetDays: row.RecurrenceOffsetDays!,
            } satisfies ValidEveryTwoMonthsControlRow;

        case "Quarterly":
            return {
                ...baseValidRow,
                recurrenceType: "Quarterly",
                quarterlyRecurrenceQuarters: row.QuarterlyRecurrenceQuarters!,
                recurrenceOffsetDays: row.RecurrenceOffsetDays!,
            } satisfies ValidQuarterlyControlRow;

        case "HalfYearly":
            return {
                ...baseValidRow,
                recurrenceType: "HalfYearly",
                halfYearlyRecurrenceHalfYears:
                    row.HalfYearlyRecurrenceHalfYears!,
                recurrenceOffsetDays: row.RecurrenceOffsetDays!,
            } satisfies ValidHalfYearlyControlRow;

        case "Yearly":
            return {
                ...baseValidRow,
                recurrenceType: "Yearly",
                recurrenceOffsetDays: row.RecurrenceOffsetDays!,
            } satisfies ValidYearlyControlRow;

        default:
            return assertNever(row.RecurrenceType);
    }
}
